import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useRef, useState } from "react";

import { LongPressProgressRing } from "./LongPressProgressRing";
import type { PartyShellPalette } from "../theme/theme";

/** Hold ~750ms without releasing to reveal (pointer capture so lift-off still counts). */
export const PARTY_PRESS_REVEAL_MS = 750;

export function PartyPressToReveal({ onReveal, label }: { onReveal: () => void; label: string }) {
  const p = (useTheme().palette as unknown as { party: PartyShellPalette }).party;
  const [progress, setProgress] = useState(0);
  const [pressed, setPressed] = useState(false);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const revealedRef = useRef(false);

  const stopRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const reset = () => {
    stopRaf();
    startRef.current = null;
    revealedRef.current = false;
    setProgress(0);
    setPressed(false);
  };

  const tick = () => {
    const start = startRef.current;
    if (start == null) return;
    const elapsed = performance.now() - start;
    const pg = Math.min(1, elapsed / PARTY_PRESS_REVEAL_MS);
    setProgress(pg);
    if (pg >= 1) {
      stopRaf();
      setPressed(false);
      if (!revealedRef.current) {
        revealedRef.current = true;
        onReveal();
      }
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    e.preventDefault();
    const el = e.currentTarget;
    try {
      el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    revealedRef.current = false;
    startRef.current = performance.now();
    setPressed(true);
    stopRaf();
    rafRef.current = requestAnimationFrame(tick);
  };

  const onPointerEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    try {
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
    } catch {
      /* ignore */
    }
    setPressed(false);
    if (revealedRef.current) return;
    const start = startRef.current;
    if (start == null) return;
    const elapsed = performance.now() - start;
    if (elapsed < PARTY_PRESS_REVEAL_MS) {
      reset();
    }
  };

  return (
    <Button
      type="button"
      variant="text"
      disableRipple
      onPointerDown={onPointerDown}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onPointerLeave={(e) => {
        if (revealedRef.current) return;
        try {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) return;
        } catch {
          /* ignore */
        }
        reset();
      }}
      sx={{
        p: 0,
        minWidth: 200,
        width: 200,
        height: 200,
        mx: "auto",
        display: "block",
        borderRadius: 999,
        bgcolor: "rgba(255,255,255,0.08)",
        position: "relative",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
        transform: pressed ? "scale(0.94)" : "scale(1)",
        boxShadow: pressed ? "inset 0 8px 24px rgba(0,0,0,0.35)" : "inset 0 0 0 transparent",
        transition: "transform 0.12s ease, box-shadow 0.12s ease",
      }}
    >
      {pressed && progress < 1 ? (
        <LongPressProgressRing
          progress={progress}
          trackStroke={`color-mix(in oklab, ${p.hot} 45%, transparent)`}
          progressStroke={p.hot}
        />
      ) : null}
      <Box
        sx={{
          position: "absolute",
          inset: 1.25,
          borderRadius: 999,
          bgcolor: p.bg2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          color: p.ink,
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          pointerEvents: "none",
        }}
      >
        <Box
          component="svg"
          width={40}
          height={40}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          aria-hidden
        >
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 018 0v3" />
        </Box>
        <Typography sx={{ mt: 0.75, fontSize: 16, textAlign: "center", px: 1, lineHeight: 1.25, fontWeight: 900 }}>
          {label}
        </Typography>
      </Box>
    </Button>
  );
}
